import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import mime from 'mime';
import { EMPTY, first, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { SortData } from 'src/app/models/common/sort-data.model';
import {
  DiaryNotePatchBody,
  DiaryNotesResponse,
  DiaryNoteWithAttachmentData,
  DownloadEvent,
  DownloadRequest
} from 'src/app/models/diary-notes/diary-note.model';
import { DiaryNotesFilter } from 'src/app/models/diary-notes/diary-notes-filter.model';
import { SortDirectionEnum, SortEnum } from 'src/app/models/enum/diary-notes.enum';
import { MandatePermissions } from 'src/app/models/enum/mandate-permissions.enum';
import { Patient, SortDirection } from 'vidis-common';
import { selectUserIsProfessional, selectUserStatePatient } from '../../store/selectors/user.selector';
import { MandateService } from '../mandate/mandate.service';
import { ToastService } from '../toast/toast.service';
import { CountResult } from 'vidis-common/common/models/count-result.model';

interface ParamsDiaryNotes {
  ssin?: string,
  fromDate?: string,
  toDate?: string,
  roles?: string[],
  tags?: string[],
  authors?: string[],
  sort?: SortEnum,
  sortDirection?: SortDirectionEnum,
  page?: number,
  pageSize?: number,
}

@Injectable({
  providedIn: 'root'
})
export class DiaryNotesService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly mandateService = inject(MandateService);
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(ToastService);

  static isDiaryNoteCompleted(diaryNoteStatus: string) {
    return diaryNoteStatus?.toLowerCase() === 'completed';
  }

  getPatientsDiaryNotes(
    ssin: string,
    activeFilters: DiaryNotesFilter,
    page: number,
    pageSize: number,
    sortData: SortData,
  ): Observable<DiaryNotesResponse> {
    return this.mandateService.hasAccess(MandatePermissions.consultDiaryNotes).pipe(
      switchMap(hasAccess =>
        hasAccess
          ? this.store.select(selectUserIsProfessional)
          : throwError(() => ({errors: [{code: 'no-access'}]}))),
      switchMap((isProfessional: boolean) => {
        let params: ParamsDiaryNotes = {};
        if (isProfessional) {
          params.ssin = ssin;
        }

        if (activeFilters.dateRange.from !== '') {
          params.fromDate = format(activeFilters.dateRange.from, 'yyyy-MM-dd');
        }

        if (activeFilters.dateRange.until !== '') {
          params.toDate = format(activeFilters.dateRange.until, 'yyyy-MM-dd');
        }

        if (activeFilters.role) {
          params.roles = [activeFilters.role];
        }

        if (activeFilters.tags) {
          params.tags = activeFilters.tags;
        }

        if (activeFilters.author) {
          params.authors = [activeFilters.author];
        }

        if (sortData.sort) {
          params.sort = SortEnum[sortData.sort];
        }

        if (sortData.order === SortDirection.ascending) {
          params.sortDirection = SortDirectionEnum.ascending;
        }

        if (page) {
          params.page = page;
        }

        if (pageSize) {
          params.pageSize = pageSize;
        }

        return this.http.get<DiaryNotesResponse>('/v1/mydiarynotes', {
          params: {...params}
        });
      })
    );
  }

  countDiaryNotes(
    ssin: string
  ): Observable<CountResult> {
    return this.mandateService.hasAccess(MandatePermissions.consultDiaryNotes).pipe(
      switchMap(hasAccess =>
        hasAccess
          ? this.store.select(selectUserIsProfessional)
          : throwError(() => ({errors: [{code: 'no-access'}]}))),
      switchMap((isProfessional: boolean) => {
        let request = {ssin: ''};
        if (isProfessional) {
          request.ssin = ssin;
        }

        return this.http.post<CountResult>('/v1/mydiarynotes/count', request);
      })
    );
  }

  fetchDiaryNoteAttachment({id, attachmentIndex, patientSsin, source, version}: DownloadRequest) {
    const params = {
      patientSsin,
      source: source ?? 'vitalink',
      version: String(version ?? ''),
    };
    return this.http.get(`/v1/diarynotes/${id}/download/${attachmentIndex}`, {
      params,
      observe: 'response',
      responseType: 'blob',

    }).pipe(catchError(err => {
      this.toastService.showError('error.errorInService');
      return throwError(() => err);
    }));
  }

  deleteDiaryNote(
    id: string,
    version: number,
    source: string,
    patientSsin: string
  ): Observable<{ diaryNote: DiaryNoteWithAttachmentData, statusCode: number }> {
    return this.http.delete(`/v1/diarynotes/${id}`, {
      params: {
        version: version,
        source: source ?? 'vitalink',
        patientSsin: patientSsin
      },
      observe: 'response'
    }).pipe(map((response: HttpResponse<DiaryNoteWithAttachmentData>) => ({
      diaryNote: response.body,
      statusCode: response.status
    })));
  }

  downloadSpecificDiaryNoteAttachment(request: DownloadRequest) {
    this.downloadAttachment$(request)
      .pipe(take(1))
      .subscribe((downloadEvent: DownloadEvent) => {
        this.handleDownload(downloadEvent);
      });
  }

  getAttachmentFileName(index: number, contentType: string): string {
    const fileExtension = contentType ? mime.getExtension(contentType) : 'unknown';
    return this.translate.instant('commons.file') + '_' + (index + 1) + '.' + fileExtension;
  }

  postDiaryNote(text: string, file: File): Observable<{}> {
    return this.getPostDiaryNoteFormData(text, file).pipe(
      first(),
      switchMap(formData => this.http.post('/v1/diarynotes', formData))
    );
  }

  private getPostDiaryNoteFormData(text: string, file: File): Observable<FormData> {
    return this.store.select(selectUserStatePatient)
      .pipe(map(patientData => this.buildFormData(text, file, patientData)));
  }

  private buildFormData(text: string, file: File, patientData: Patient): FormData {
    let formData: FormData = new FormData();
    let diaryNoteBody: DiaryNotePatchBody = {
      language: this.translate.currentLang,
      subject: {
        firstName: patientData.firstName,
        lastName: patientData.lastName,
        ssin: patientData.ssin
      },
      content: text,
      contentType: 'plaintext',
    };

    formData.append('attachments', file);
    formData.append('diaryNote', new Blob([JSON.stringify(diaryNoteBody)], {type: 'application/json'}));
    return formData;
  }

  handleDownload(evt: DownloadEvent) {
    const url = URL.createObjectURL(evt.content);
    const a = document.createElement('a');
    a.href = url;
    a.download = evt.fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  downloadAttachment$(request: DownloadRequest): Observable<DownloadEvent> {
    return this.fetchDiaryNoteAttachment(request).pipe(
      map((resp: HttpResponse<Blob>) => {
        const blob = resp.body;
        if (!blob || blob.size === 0) {
          throw new Error('EMPTY_BLOB');
        }
        const mimeType = resp.headers.get('Content-Type') || blob.type || 'application/octet-stream';
        const fileName = this.getAttachmentFileName(request.attachmentIndex, mimeType);

        return {fileName: fileName, content: blob, mimeType};
      }),
      catchError(() => {
        this.toastService.showError('error.error-page.general');
        return EMPTY;
      })
    );
  }
}

